import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const EditAdmissionForm = () => {
  const navigate = useNavigate();
  const { admissionId } = useParams();
  const location = useLocation();
  const isViewMode = location.pathname.includes('/view-admission/');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [colleges, setColleges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [cannotEdit, setCannotEdit] = useState(false);

  const [formData, setFormData] = useState({
    studentName: '',
    mobile: '',
    email: '',
    fatherName: '',
    fatherMobile: '',
    selectedCourses: [],
    tenthPercentage: '',
    twelfthPercentage: '',
    graduationPercentage: '',
    itiDiploma: '',
    collegeName: '',
    address: '',
    aadharCard: '',
    collegeId: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAdmissionData();
    fetchColleges();
  }, [admissionId]);

  const fetchAdmissionData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/admission-data/${admissionId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        },
      });
      
      const admission = response.data.data;
      console.log(admission, "admission")
      if (!admission) {
        setNotFound(true);
        return;
      }

      if (admission.paymentInfo?.status !== 'pending') {
        setCannotEdit(true);
        return;
      }

      setFormData({
        studentName: admission.studentName || '',
        mobile: admission.mobile || '',
        email: admission.email || '',
        fatherName: admission.fatherName || '',
        fatherMobile: admission.fatherMobile || '',
        selectedCourses: admission.selectedCourses?.map(course => course._id) || [],
        tenthPercentage: admission.tenthPercentage || '',
        twelfthPercentage: admission.twelfthPercentage || '',
        graduationPercentage: admission.graduationPercentage || '',
        itiDiploma: admission.itiDiploma || '',
        collegeName: admission.collegeName || '',
        address: admission.address || '',
        aadharCard: admission.aadharCard || '',
        collegeId: admission.collegeId?._id || admission.collegeId || ''
      });

      if (admission.collegeId?._id || admission.collegeId) {
        const collegeId = admission.collegeId?._id || admission.collegeId;
        setCourses(admission.collegeId?.courses || []);
      }
    } catch (error) {
      console.error("Error fetching admission data:", error);
      setNotFound(true);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/all-colleges`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        },
      });
      setColleges(response.data.data);
    } catch (error) {
      console.error("Error fetching colleges:", error);
    }
  };

  const handleCollegeChange = (e) => {
    const collegeId = e.target.value;
    setFormData({
      ...formData,
      collegeId,
      selectedCourses: []
    });
    
    if (collegeId) {
      const selectedCollege = colleges.find(college => college._id === collegeId);
      if (selectedCollege && selectedCollege.courses) {
        setCourses(selectedCollege.courses);
      } else {
        setCourses([]);
      }
      
      setFormData(prev => ({
        ...prev,
        collegeId,
        collegeName: selectedCollege?.name || '',
        selectedCourses: []
      }));
    } else {
      setCourses([]);
    }
  };

  const handleCourseChange = (courseId) => {
    const isSelected = formData.selectedCourses.includes(courseId);
    if (isSelected) {
      setFormData({
        ...formData,
        selectedCourses: formData.selectedCourses.filter(id => id !== courseId)
      });
    } else {
      setFormData({
        ...formData,
        selectedCourses: [...formData.selectedCourses, courseId]
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.collegeId) newErrors.collegeId = 'College is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/admission/${admissionId}`,
        { admissionData: formData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      navigate("/admission-manager");
    } catch (error) {
      console.error("Error updating admission form:", error);
      alert(error.response?.data?.message || "Failed to update admission form");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">Loading admission data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex bg-gray-100">
        <Sidebar />
        <div className="flex-1 p-6">
          <h1 className="text-2xl font-bold mb-6">Edit Admission Form</h1>
          <form onSubmit={handleSubmit}>
            <p className="text-gray-600 mb-6">The admission form you're trying to edit doesn't exist.</p>
            <button
              onClick={() => navigate("/admission-manager")}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
            >
              Back to Admission Manager
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (cannotEdit) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <h2 className="text-2xl font-bold text-yellow-600 mb-4">Cannot Edit Admission</h2>
              <p className="text-gray-600 mb-6">This admission form cannot be edited because payment has been completed.</p>
              <button
                onClick={() => navigate("/admission-manager")}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                Back to Admission Manager
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-6">
        <h1 className="text-2xl font-bold mb-6">{isViewMode ? "View Admission Form" : "Edit Admission Form"}</h1>
        <form onSubmit={handleSubmit}>
          {/* Student Name */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Student Name</label>
            <input
              type="text"
              name="studentName"
              value={formData.studentName}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.studentName ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter student name"
            />
            {errors.studentName && <p className="text-red-500">{errors.studentName}</p>}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.email ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter email"
            />
            {errors.email && <p className="text-red-500">{errors.email}</p>}
          </div>

          {/* Mobile */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Mobile</label>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.mobile ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              maxLength="10"
              placeholder="Enter mobile number"
            />
            {errors.mobile && <p className="text-red-500">{errors.mobile}</p>}
          </div>

          {/* Father Name */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Father Name</label>
            <input
              type="text"
              name="fatherName"
              value={formData.fatherName}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.fatherName ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter father name"
            />
            {errors.fatherName && <p className="text-red-500">{errors.fatherName}</p>}
          </div>

          {/* Father Mobile */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Father Mobile</label>
            <input
              type="text"
              name="fatherMobile"
              value={formData.fatherMobile}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.fatherMobile ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              maxLength="10"
              placeholder="Enter father mobile number"
            />
            {errors.fatherMobile && <p className="text-red-500">{errors.fatherMobile}</p>}
          </div>

          {/* College */}
          <div className="mb-4">
            <label className="block font-bold mb-2">College *</label>
            <select
              name="collegeId"
              value={formData.collegeId}
              onChange={handleCollegeChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.collegeId ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Select College</option>
              {colleges.map((college) => (
                <option key={college._id} value={college._id}>
                  {college.name}
                </option>
              ))}
            </select>
            {errors.collegeId && <p className="text-red-500">{errors.collegeId}</p>}
          </div>

          {/* Courses */}
          {courses.length > 0 && (
            <div className="mb-4">
              <label className="block font-bold mb-2">Courses</label>
              <div className="border border-gray-300 rounded-md p-3 h-40 overflow-y-auto bg-gray-50">
                <div className="space-y-2">
                  {courses.map((course) => (
                    <label key={course._id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.selectedCourses.includes(course._id)}
                        onChange={() => handleCourseChange(course._id)}
                        disabled={isViewMode}
                        className="mr-2"
                      />
                      {course.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 10th Percentage */}
          <div className="mb-4">
            <label className="block font-bold mb-2">10th Percentage</label>
            <input
              type="number"
              name="tenthPercentage"
              value={formData.tenthPercentage}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter 10th percentage"
            />
          </div>

          {/* 12th Percentage */}
          <div className="mb-4">
            <label className="block font-bold mb-2">12th Percentage</label>
            <input
              type="number"
              name="twelfthPercentage"
              value={formData.twelfthPercentage}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter 12th percentage"
            />
          </div>

          {/* Graduation Percentage */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Graduation Percentage</label>
            <input
              type="number"
              name="graduationPercentage"
              value={formData.graduationPercentage}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter graduation percentage"
            />
          </div>

          {/* ITI/Diploma */}
          <div className="mb-4">
            <label className="block font-bold mb-2">ITI/Diploma</label>
            <input
              type="text"
              name="itiDiploma"
              value={formData.itiDiploma}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Enter ITI/Diploma details"
            />
          </div>

          {/* Aadhar Card */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Aadhar Card</label>
            <input
              type="text"
              name="aadharCard"
              value={formData.aadharCard}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              maxLength="12"
              placeholder="Enter Aadhar card number"
            />
          </div>

          {/* Address */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              disabled={isViewMode}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.address ? 'border-red-500' : ''} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              rows="3"
              placeholder="Enter address"
            />
            {errors.address && <p className="text-red-500">{errors.address}</p>}
          </div>

          {/* Submit Button */}
          {!isViewMode && (
            <button
              type="submit"
              disabled={loading}
              className="bg-blue text-white px-6 py-2 rounded-md shadow-md hover:bg-blue-600"
            >
              {loading ? "Updating..." : "Update Admission Form"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditAdmissionForm;
